import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { useToast } from './ui/use-toast';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const NewClientModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    client_type: 'Business',
    industry: '',
    entity_type: '',
    business_name: '',
    dba: '',
    business_start_date: '',
    monthly_revenue: '',
    federal_tax_id: '',
    dob: '',
    ssn: '',
    expected_grade: '',
    website: '',
    fax: '',
    business_phone: '',
    cell_phone: '',
    home_phone: '',
    email: '',
    cross_collateralize: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('mcaToken');
      
      if (!token) {
        toast({ title: 'Error', description: 'Please log in again', variant: 'destructive' });
        return;
      }
      
      const response = await axios.post(`${API_BASE}/clients`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Client created successfully' });
      onClose();
      navigate(`/clients/${response.data.id}`);
    } catch (error) {
      console.error('Client creation error:', error.response || error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create client', variant: 'destructive' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg p-8 max-w-4xl w-full mx-4 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold">New Client</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Info */}
          <div>
            <h3 className="text-xl font-semibold mb-4">Personal Info</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">First Name *</label>
                <Input
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  placeholder="First Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Last Name *</label>
                <Input
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  placeholder="Last Name"
                />
              </div>
            </div>
          </div>

          {/* General Info */}
          <div>
            <h3 className="text-xl font-semibold mb-4">General Info</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Client Type *</label>
                <select
                  required
                  value={formData.client_type}
                  onChange={(e) => setFormData({...formData, client_type: e.target.value})}
                  className="w-full border rounded p-2"
                >
                  <option value="Business">Business</option>
                  <option value="Individual">Individual</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Industry *</label>
                <select
                  required
                  value={formData.industry}
                  onChange={(e) => setFormData({...formData, industry: e.target.value})}
                  className="w-full border rounded p-2"
                >
                  <option value="">Select Industry</option>
                  <option value="Transportation">Transportation</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Restaurant">Restaurant</option>
                  <option value="Retail">Retail</option>
                  <option value="Technology">Technology</option>
                  <option value="Construction">Construction</option>
                  <option value="Services">Services</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Entity Type *</label>
                <select
                  required
                  value={formData.entity_type}
                  onChange={(e) => setFormData({...formData, entity_type: e.target.value})}
                  className="w-full border rounded p-2"
                >
                  <option value="">Select Type</option>
                  <option value="LLC">LLC</option>
                  <option value="Inc">Inc</option>
                  <option value="Corporation">Corporation</option>
                  <option value="Sole Proprietorship">Sole Proprietorship</option>
                  <option value="Partnership">Partnership</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Business Name *</label>
                <Input
                  required
                  value={formData.business_name}
                  onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Doing Business As *</label>
                <Input
                  required
                  value={formData.dba}
                  onChange={(e) => setFormData({...formData, dba: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Business Start Date *</label>
                <Input
                  required
                  type="date"
                  value={formData.business_start_date}
                  onChange={(e) => setFormData({...formData, business_start_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Monthly Revenue</label>
                <Input
                  value={formData.monthly_revenue}
                  onChange={(e) => setFormData({...formData, monthly_revenue: e.target.value})}
                  placeholder="$100,000.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Federal Tax ID *</label>
                <Input
                  required
                  value={formData.federal_tax_id}
                  onChange={(e) => setFormData({...formData, federal_tax_id: e.target.value})}
                  placeholder="12-3456789"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Date of Birth (DOB)</label>
                <Input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({...formData, dob: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">SSN</label>
                <Input
                  value={formData.ssn}
                  onChange={(e) => setFormData({...formData, ssn: e.target.value})}
                  placeholder="123-45-6789"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Expected Grade</label>
                <select
                  value={formData.expected_grade}
                  onChange={(e) => setFormData({...formData, expected_grade: e.target.value})}
                  className="w-full border rounded p-2"
                >
                  <option value="">Select Grade</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Website</label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">FAX</label>
                <Input
                  value={formData.fax}
                  onChange={(e) => setFormData({...formData, fax: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Business Phone *</label>
                <Input
                  required
                  value={formData.business_phone}
                  onChange={(e) => setFormData({...formData, business_phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Cell Phone</label>
                <Input
                  value={formData.cell_phone}
                  onChange={(e) => setFormData({...formData, cell_phone: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Home Phone</label>
                <Input
                  value={formData.home_phone}
                  onChange={(e) => setFormData({...formData, home_phone: e.target.value})}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">Email *</label>
              <Input
                required
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="contact@business.com"
              />
            </div>

            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                checked={formData.cross_collateralize}
                onChange={(e) => setFormData({...formData, cross_collateralize: e.target.checked})}
                className="w-4 h-4"
              />
              <label className="text-sm font-medium">Cross-collateralize</label>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">Create Client</Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewClientModal;